import { AuthMethod } from "@/queries/types";
import { DSTestLandingPage } from "@hundred5/design-system";
import { Trans, useLingui } from "@lingui/react/macro";
import React, { useState } from "react";
import EmailLoginForm from "./email-login-form";

type LoginMethod = AuthMethod | "test";

interface Props {
  /** Buttons that will be shown to the user. */
  allowedMethods: LoginMethod[];
  /** Called when the user clicks on one of the buttons. Returned promise should be pending while logging in. */
  logIn: (method: LoginMethod) => Promise<void>;
  logInWithEmail: (email: string) => Promise<void>;
}

const order: string[] = [
  "test",
  "linkedin",
  "github",
  "email",
  "email-continue",
];

export function LoginForm({ allowedMethods, logIn, logInWithEmail }: Props) {
  const { t } = useLingui();
  const [loading, setLoading] = useState(false);

  const handleLogIn = (method: LoginMethod) => {
    if (!loading) {
      setLoading(true);
      logIn(method).catch(() => setLoading(false));
    }
  };

  const orderedMethods = allowedMethods.sort(
    (a, b) => order.indexOf(a) - order.indexOf(b),
  );
  const loggedIn = orderedMethods.length === 1;

  return (
    <DSTestLandingPage.Login
      label={!loggedIn ? t`Log in with` : undefined}
      disclaimer={
        <Trans>
          By proceeding, you agree to our{" "}
          <a
            href="https://toggl.com/hire/candidate-terms"
            target="_blank"
            rel="noreferrer noopener"
          >
            Terms & Conditions
          </a>{" "}
          and{" "}
          <a
            href="https://toggl.com/legal/hire/privacy"
            target="_blank"
            rel="noreferrer noopener"
          >
            Privacy Policy
          </a>
          . You will also receive marketing emails from us, which you can opt
          out of anytime.
        </Trans>
      }
    >
      {orderedMethods.map((method, index) => (
        <React.Fragment key={index}>
          {method === "test" && (
            <DSTestLandingPage.LoginButton
              method="other"
              primary={index === 0}
              label={t`Log in for testing`}
              onClick={() => handleLogIn(method)}
              disabled={loading}
            />
          )}

          {(method === "linkedin" || method === "github") && (
            <DSTestLandingPage.LoginButton
              method={method}
              primary={index === 0}
              onClick={() => handleLogIn(method)}
              disabled={loading}
            />
          )}

          {method === "email" && (
            <>
              {index !== 0 && (
                <DSTestLandingPage.LoginSeparator label={t`or`} />
              )}
              <EmailLoginForm primary={index === 0} onSubmit={logInWithEmail} />
            </>
          )}
        </React.Fragment>
      ))}
    </DSTestLandingPage.Login>
  );
}
